// Migration Note: Updated to handle material creation asynchronously, returning promises to avoid blocking.
// Added options parameter to support more flexible material creation and added disposeMaterialCache for proper resource cleanup.
import { useEffect, useState } from 'react';
import { DoubleSide, MeshBasicMaterial, Texture, TextureLoader } from 'three';

const materialCache: Record<string, Record<number, Record<string, MeshBasicMaterial>>> = {};

const textureLoader = new TextureLoader();
const loadTexture = async (path: string) =>
  new Promise((resolve, reject) => {
    textureLoader.load(path, resolve, undefined, reject);
  });

export const getCachedMaterial = (
  color: string,
  opacity: number,
  options?: { depthTest?: boolean },
): MeshBasicMaterial => {
  const depthTest = options?.depthTest ?? true;
  const optionsKey = `${depthTest}`;

  if (!materialCache[color]) {
    materialCache[color] = {};
  }
  if (!materialCache[color][opacity]) {
    materialCache[color][opacity] = {};
  }

  let material = materialCache[color][opacity][optionsKey];
  if (!material) {
    material = new MeshBasicMaterial({
      transparent: opacity < 1,
      color: color,
      opacity,
      depthTest,
    });
    materialCache[color][opacity][optionsKey] = material;
  }
  return material;
};

const materialPromiseCache: Record<string, Promise<MeshBasicMaterial>> = {};

export const getCachedMaterialAsync = (
  textureUrl: string,
  opacity: number,
  options?: { depthWrite?: boolean; alphaTest?: number; depthTest?: boolean },
): Promise<MeshBasicMaterial> => {
  const optionsKey = options
    ? `${options.depthWrite}_${options.alphaTest}_${options.depthTest}`
    : '';
  const key = `${textureUrl}_${opacity}_${optionsKey}`;

  if (!materialPromiseCache[key]) {
    materialPromiseCache[key] = (async () => {
      const texture = (await loadTexture(textureUrl)) as Texture;
      if (!texture) throw new Error('no texture found');
      return new MeshBasicMaterial({
        map: texture as Texture,
        side: DoubleSide,
        transparent: true,
        depthTest: false,
        opacity,
        ...options,
      });
    })();
  }
  return materialPromiseCache[key];
};

export const useLoadTexturedMaterial = (
  textureUrl: string,
  opacity: number,
  options?: { depthWrite?: boolean; alphaTest?: number; depthTest?: boolean },
): MeshBasicMaterial | undefined => {
  const [material, setMaterial] = useState<MeshBasicMaterial | undefined>(undefined);
  useEffect(() => {
    let active = true;
    getCachedMaterialAsync(textureUrl, opacity, options).then((mat) => {
      if (active) {
        setMaterial(mat);
      }
    });
    return () => {
      active = false; // Prevents race condition updates
    };
  }, [textureUrl, opacity, options?.depthWrite, options?.alphaTest, options?.depthTest]);
  return material;
};

export const disposeMaterialCache = (): void => {
  // Clear synchronous color materials
  for (const key of Object.keys(materialCache)) {
    const opacityMap = materialCache[key];
    if (opacityMap) {
      for (const opacityKey of Object.keys(opacityMap)) {
        const optionsMap = opacityMap[Number(opacityKey)];
        if (optionsMap) {
          for (const optionsKey of Object.keys(optionsMap)) {
            const material = optionsMap[optionsKey];
            if (material) {
              material.dispose(); // Solid colors have no map to dispose
            }
          }
        }
      }
    }
    delete materialCache[key];
  }

  // Clear asynchronous textured materials
  for (const key of Object.keys(materialPromiseCache)) {
    materialPromiseCache[key]
      .then((material) => {
        if (material.map) {
          material.map.dispose();
        }
        material.dispose();
      })
      .catch(() => {}); // Swallow any loading errors on teardown
    delete materialPromiseCache[key];
  }
};
